import { apiFetch } from "../api/client";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Link } from "react-router-dom";

const API =
  process.env.REACT_APP_API_URL ||
  "https://api.wirelesspress.org";

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatDate(value) {
  if (!value) {
    return "Not available";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
}

function formatLabel(value) {
  if (!value) {
    return "Unknown";
  }

  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function ThreatAnalyticsPage() {
  const [analytics, setAnalytics] =
    useState(null);

  const [days, setDays] =
    useState(30);

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] =
    useState("");

  const loadAnalytics = useCallback(
    async (silent = false) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const response = await apiFetch(`/threat-intel/analytics?days=${days}`
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.detail ||
              "Unable to load threat intelligence analytics."
          );
        }

        setAnalytics(data);
      } catch (loadError) {
        console.error(
          "Threat analytics error:",
          loadError
        );

        setError(
          loadError.message ||
            "Unable to load threat intelligence analytics."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [days]
  );

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => {
    const timer = window.setInterval(
      () => loadAnalytics(true),
      60000
    );

    return () =>
      window.clearInterval(timer);
  }, [loadAnalytics]);

  const summary =
    analytics?.summary || {};

  const timeline =
    analytics?.timeline || [];

  const categories =
    analytics?.top_threat_categories || [];

  const actors =
    analytics?.top_threat_actors || [];

  const campaigns =
    analytics?.top_campaigns || [];

  const malwareFamilies =
    analytics?.top_malware_families || [];

  const devices =
    analytics?.top_affected_devices || [];

  const incidents =
    analytics?.top_enriched_incidents || [];

  const feeds =
    analytics?.feed_effectiveness || [];

  const matchedFields =
    analytics?.top_matched_fields || [];

  const maxTimelineValue = useMemo(
    () =>
      Math.max(
        ...timeline.map(
          (item) =>
            Number(item.new_indicators || 0) +
            Number(item.ioc_matches || 0)
        ),
        1
      ),
    [timeline]
  );

  const metrics = [
    {
      label: "Total Indicators",
      value: summary.total_indicators,
      detail: `${formatNumber(
        summary.active_indicators
      )} active`,
    },
    {
      label: `New in ${days} Days`,
      value: summary.indicators_in_period,
      detail: "Indicators ingested",
    },
    {
      label: "Total Matches",
      value: summary.total_matches,
      detail: `${formatNumber(
        summary.open_matches
      )} open`,
    },
    {
      label: "Enriched Incidents",
      value: summary.enriched_incidents,
      detail: "Linked to IOC matches",
    },
    {
      label: "Affected Devices",
      value:
        summary.affected_devices ||
        summary.affected_device_uuids,
      detail: "Observed endpoints",
    },
    {
      label: "Affected Companies",
      value: summary.affected_companies,
      detail: "Impacted tenants",
    },
    {
      label: "Average Confidence",
      value: `${summary.average_confidence || 0}%`,
      detail: "IOC confidence score",
    },
    {
      label: "Expired Indicators",
      value: summary.expired_indicators,
      detail: "Review or retire",
    },
  ];

  return (
    <div className="threatAnalyticsPage">
      <header className="threatAnalyticsHeader">
        <div>
          <p className="threatAnalyticsEyebrow">
            WirelessPress Threat Intelligence
          </p>

          <h1>
            Threat Intelligence Analytics
          </h1>

          <p>
            Analyze IOC growth, match activity,
            affected endpoints, incidents,
            feeds, threat actors, campaigns,
            and malware families.
          </p>
        </div>

        <div className="threatAnalyticsActions">
          <Link
            to="/threat-intelligence"
            className="threatAnalyticsSecondaryButton"
          >
            Dashboard
          </Link>

          <Link
            to="/threat-intelligence/indicators"
            className="threatAnalyticsSecondaryButton"
          >
            IOC Explorer
          </Link>

          <select
            value={days}
            onChange={(event) =>
              setDays(
                Number(event.target.value)
              )
            }
          >
            <option value="7">
              Last 7 days
            </option>

            <option value="30">
              Last 30 days
            </option>

            <option value="90">
              Last 90 days
            </option>

            <option value="365">
              Last 365 days
            </option>
          </select>

          <button
            type="button"
            className="threatAnalyticsPrimaryButton"
            disabled={refreshing}
            onClick={() =>
              loadAnalytics(true)
            }
          >
            {refreshing
              ? "Refreshing..."
              : "Refresh"}
          </button>
        </div>
      </header>

      {error ? (
        <div className="threatAnalyticsError">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="threatAnalyticsLoading">
          Loading threat analytics...
        </div>
      ) : null}

      {!loading && analytics ? (
        <>
          <section className="threatAnalyticsMetrics">
            {metrics.map((metric) => (
              <article key={metric.label}>
                <span>{metric.label}</span>

                <strong>
                  {typeof metric.value ===
                  "string"
                    ? metric.value
                    : formatNumber(
                        metric.value
                      )}
                </strong>

                <small>
                  {metric.detail}
                </small>
              </article>
            ))}
          </section>

          <section className="threatAnalyticsPanel">
            <div className="threatAnalyticsPanelHeader">
              <div>
                <span>
                  IOC ACTIVITY
                </span>

                <h2>
                  Indicator and Match Timeline
                </h2>
              </div>
            </div>

            <div className="threatAnalyticsTimeline">
              {timeline.map((item) => {
                const indicators =
                  Number(
                    item.new_indicators || 0
                  );

                const matches =
                  Number(
                    item.ioc_matches || 0
                  );

                return (
                  <article
                    key={item.event_date}
                  >
                    <div className="threatAnalyticsTimelineDate">
                      {item.event_date}
                    </div>

                    <div className="threatAnalyticsTimelineBars">
                      <div>
                        <span>
                          New IOCs
                        </span>

                        <div>
                          <i
                            style={{
                              width: `${
                                (indicators /
                                  maxTimelineValue) *
                                100
                              }%`,
                            }}
                          />
                        </div>

                        <strong>
                          {formatNumber(
                            indicators
                          )}
                        </strong>
                      </div>

                      <div>
                        <span>
                          Matches
                        </span>

                        <div>
                          <i
                            style={{
                              width: `${
                                (matches /
                                  maxTimelineValue) *
                                100
                              }%`,
                            }}
                          />
                        </div>

                        <strong>
                          {formatNumber(
                            matches
                          )}
                        </strong>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="threatAnalyticsGrid">
            <AnalyticsList
              title="Top Threat Categories"
              items={categories}
              labelKey="threat_category"
            />

            <AnalyticsList
              title="Top Threat Actors"
              items={actors}
              labelKey="threat_actor"
            />

            <AnalyticsList
              title="Top Campaigns"
              items={campaigns}
              labelKey="campaign"
            />

            <AnalyticsList
              title="Top Malware Families"
              items={malwareFamilies}
              labelKey="malware_family"
            />
          </section>

          <section className="threatAnalyticsGrid">
            <article className="threatAnalyticsPanel">
              <div className="threatAnalyticsPanelHeader">
                <div>
                  <span>
                    ENDPOINT IMPACT
                  </span>

                  <h2>
                    Most Affected Devices
                  </h2>
                </div>
              </div>

              <div className="threatAnalyticsTableWrapper">
                <table className="threatAnalyticsTable">
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>Platform</th>
                      <th>Matches</th>
                      <th>Unique IOCs</th>
                      <th>Latest</th>
                    </tr>
                  </thead>

                  <tbody>
                    {devices.length === 0 ? (
                      <tr>
                        <td colSpan="5">
                          No affected devices.
                        </td>
                      </tr>
                    ) : null}

                    {devices.map((device) => (
                      <tr key={device.device_id}>
                        <td>
                          <strong>
                            {device.hostname ||
                              device.device_uuid}
                          </strong>
                        </td>

                        <td>
                          {device.platform ||
                            "Unknown"}
                        </td>

                        <td>
                          {formatNumber(
                            device.match_count
                          )}
                        </td>

                        <td>
                          {formatNumber(
                            device.unique_indicator_count
                          )}
                        </td>

                        <td>
                          {formatDate(
                            device.latest_match
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="threatAnalyticsPanel">
              <div className="threatAnalyticsPanelHeader">
                <div>
                  <span>
                    INCIDENT ENRICHMENT
                  </span>

                  <h2>
                    Top Enriched Incidents
                  </h2>
                </div>
              </div>

              <div className="threatAnalyticsTableWrapper">
                <table className="threatAnalyticsTable">
                  <thead>
                    <tr>
                      <th>Incident</th>
                      <th>Severity</th>
                      <th>Status</th>
                      <th>Matches</th>
                      <th>Unique IOCs</th>
                    </tr>
                  </thead>

                  <tbody>
                    {incidents.length === 0 ? (
                      <tr>
                        <td colSpan="5">
                          No enriched incidents.
                        </td>
                      </tr>
                    ) : null}

                    {incidents.map((incident) => (
                      <tr key={incident.incident_id}>
                        <td>
                          <strong>
                            {
                              incident.incident_number
                            }
                          </strong>

                          <small>
                            {incident.title}
                          </small>
                        </td>

                        <td>
                          {formatLabel(
                            incident.severity
                          )}
                        </td>

                        <td>
                          {formatLabel(
                            incident.status
                          )}
                        </td>

                        <td>
                          {formatNumber(
                            incident.match_count
                          )}
                        </td>

                        <td>
                          {formatNumber(
                            incident.unique_indicator_count
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>
          </section>

          <section className="threatAnalyticsGrid">
            <article className="threatAnalyticsPanel">
              <div className="threatAnalyticsPanelHeader">
                <div>
                  <span>
                    FEED PERFORMANCE
                  </span>

                  <h2>
                    Feed Effectiveness
                  </h2>
                </div>
              </div>

              <div className="threatAnalyticsTableWrapper">
                <table className="threatAnalyticsTable">
                  <thead>
                    <tr>
                      <th>Feed</th>
                      <th>Indicators</th>
                      <th>Matches</th>
                      <th>Incidents</th>
                      <th>Devices</th>
                    </tr>
                  </thead>

                  <tbody>
                    {feeds.map((feed) => (
                      <tr key={feed.feed_id}>
                        <td>
                          <strong>
                            {feed.feed_name}
                          </strong>

                          <small>
                            {formatLabel(
                              feed.feed_type
                            )}
                          </small>
                        </td>

                        <td>
                          {formatNumber(
                            feed.indicator_count
                          )}
                        </td>

                        <td>
                          {formatNumber(
                            feed.match_count
                          )}
                        </td>

                        <td>
                          {formatNumber(
                            feed.enriched_incident_count
                          )}
                        </td>

                        <td>
                          {formatNumber(
                            feed.affected_device_count
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>

            <AnalyticsList
              title="Top Matched Fields"
              items={matchedFields}
              labelKey="matched_field"
              countKey="match_count"
            />
          </section>

          <footer className="threatAnalyticsFooter">
            Last generated:{" "}
            {formatDate(
              analytics.generated_at
            )}
          </footer>
        </>
      ) : null}
    </div>
  );
}

function AnalyticsList({
  title,
  items,
  labelKey,
  countKey = "indicator_count",
}) {
  return (
    <article className="threatAnalyticsPanel">
      <div className="threatAnalyticsPanelHeader">
        <div>
          <span>
            THREAT INTELLIGENCE
          </span>

          <h2>{title}</h2>
        </div>
      </div>

      <div className="threatAnalyticsList">
        {items.length === 0 ? (
          <p>
            No data available.
          </p>
        ) : null}

        {items.map((item, index) => (
          <div
            key={`${item[labelKey]}-${index}`}
          >
            <span>
              {formatLabel(
                item[labelKey]
              )}
            </span>

            <strong>
              {formatNumber(
                item[countKey]
              )}
            </strong>

            {item.match_count !==
            undefined ? (
              <small>
                {formatNumber(
                  item.match_count
                )}{" "}
                matches
              </small>
            ) : null}
          </div>
        ))}
      </div>
    </article>
  );
}

export default ThreatAnalyticsPage;
